import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogTitle,
  Card,
  CardHeader,
  DialogActions,
  Button,
  MenuItem,
  DialogContent,
  CardContent,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState, useCallback } from "react";
import {
  useForm,
  useWatch,
  type Control,
  type Resolver,
  type DefaultValues,
  type UseFormSetValue,
} from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { z } from "zod";

import {
  AutosendOptionType,
  validateAcrossFields,
  CriteriaType,
  dateForForm,
  EvaluationCriteriaOutput,
  EvaluationCriteriaSchema,
} from "@stw/yup-validators";

import {
  ScheduleCampaignPartialInitialSendMutation,
  ScheduleCampaignPartialInitialSendMutationVariables,
  StartCampaignPartialInitialSendMutation,
  StartCampaignPartialInitialSendMutationVariables,
  CancelCampaignPartialEvaluationMutation,
  CancelCampaignPartialEvaluationMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfDateTimePicker,
  RhfCurrencyField,
  RhfPercentageField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import {
  GET_CAMPAIGN_FOR_VIEW,
  SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND,
  START_CAMPAIGN_PARTIAL_INITIAL_SEND,
  CANCEL_CAMPAIGN_PARTIAL_EVALUATION,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { SendSummaryCard } from "~frontend/routes/subaccounts/campaignView/initialSend/SendSummaryCard";
import { criteriaTypeToLabel } from "~frontend/routes/subaccounts/campaignView/initialSend/constants";
import { CampaignForView } from "~frontend/routes/subaccounts/campaignView/types";
import {
  formatShortTime,
  timezoneDisplayName,
} from "~frontend/util/dateFormat";
import { useTextingHours } from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

const SCHEDULE_SEND_WINDOW_DAYS = 14;

// A scheduled send may not be further out than SCHEDULE_SEND_WINDOW_DAYS.
function isWithinScheduleWindow(d: Date | null | undefined): boolean {
  return (
    d == null ||
    d <=
      DateTime.now()
        .plus({ days: SCHEDULE_SEND_WINDOW_DAYS })
        .endOf("day")
        .toJSDate()
  );
}

// minRoi is stored as a decimal (e.g. 0.25 for 25%) by RhfPercentageField, so a
// whole-number percentage means value * 100 is an integer. The epsilon guards
// against float error (e.g. 0.29 * 100 === 28.999999999999996).
function isWholePercentage(value: number): boolean {
  const percentage = value * 100;
  return Math.abs(percentage - Math.round(percentage)) < 1e-9;
}

// Required whole-number count of texts to send.
const qtySchema = z.coerce
  .number({
    required_error: "Required",
    invalid_type_error: "Must be a number",
  })
  .int("Must be a whole number")
  .min(1, "Must be at least 1");

// Optional scheduled send time: must be in the future and within the window.
const scheduleForSchema = z.coerce
  .date()
  .nullish()
  .default(null)
  .refine(d => d == null || d >= new Date(), "Must be in the future")
  .refine(
    isWithinScheduleWindow,
    `Must be within the next ${SCHEDULE_SEND_WINDOW_DAYS} days`
  );

// A required date that must be in the future.
const futureDateSchema = dateForForm.refine(
  d => d > new Date(),
  "Must be in the future"
);

// Optional ROI percentage (stored as a decimal); blank/unset becomes 0.
const optionalMinRoiSchema = z.coerce
  .number()
  .nullish()
  .refine(v => v == null || v >= 0, { message: "Must be a positive value" })
  .refine(v => v == null || isWholePercentage(v), "Must be a whole number")
  .transform(v => v ?? 0);

// A positive number where a cleared field (blank) is treated as unset (null),
// but an explicit 0 is rejected. Blank/null is mapped to undefined before
// coercion so it stays optional, rather than coercing "" to 0.
function optionalNonNegativeNumber({
  isIntegerField = false,
}: {
  isIntegerField?: boolean;
}) {
  const base = z.coerce.number({ invalid_type_error: "Must be a number" });
  const positive = (isIntegerField ? base.int("Must be a whole number") : base)
    .min(1, "Must be one or greater")
    .optional();
  return z
    .preprocess(v => (v === "" || v == null ? undefined : v), positive)
    .transform(v => v ?? null);
}

const PartialSendNoAutosendSchema = z.object({
  hasAutosend: z.literal(false),
  qty: qtySchema,
  scheduleFor: scheduleForSchema,
});

const autosendBase = {
  hasAutosend: z.literal(true),
  qty: qtySchema,
  scheduleFor: scheduleForSchema,
  dontSendAfter: futureDateSchema,
  variantIds: z.array(z.string().uuid()).min(1),
  evaluationDelayInMin: z.coerce.number().int().optional().default(0),
  autosendOption: z
    .object({
      type: z.nativeEnum(AutosendOptionType),
      textsToSend: z.coerce
        .number({ message: "Must be a number" })
        .int("Must be a whole number")
        .min(1, "Must be at least 1")
        .optional(),
    })
    .default({ type: AutosendOptionType.SEND_TO_FULL }),
  minNumberOfDonations: optionalNonNegativeNumber({ isIntegerField: true }),
  excludeDonationsOverInCents: optionalNonNegativeNumber({}),
};

const PartialSendFirstToMinRoiSchema = z.object({
  ...autosendBase,
  type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
  minRoi: z.coerce
    .number({
      message: "Required",
    })
    .gt(0, "Must be greater than 0")
    .refine(isWholePercentage, "Must be a whole number"),
  evaluationDelayInMin: z.coerce.number().int().optional().default(180),
  evaluationExpiresAt: futureDateSchema,
});

const PartialSendHighestRoiAtTimeSchema = z.object({
  ...autosendBase,
  type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
  minRoi: optionalMinRoiSchema,
  evaluationExpiresAt: futureDateSchema,
});

const PartialSendVariantReachedMinRoiSchema = z.object({
  ...autosendBase,
  type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
  minRoi: optionalMinRoiSchema,
});

type PartialSendFormValues =
  | z.infer<typeof PartialSendNoAutosendSchema>
  | z.infer<typeof PartialSendFirstToMinRoiSchema>
  | z.infer<typeof PartialSendHighestRoiAtTimeSchema>
  | z.infer<typeof PartialSendVariantReachedMinRoiSchema>;

const criteriaSchemaByType = {
  [CriteriaType.FIRST_TO_MIN_ROI]: PartialSendFirstToMinRoiSchema,
  [CriteriaType.VARIANT_REACHED_MIN_ROI]: PartialSendVariantReachedMinRoiSchema,
  [CriteriaType.HIGHEST_ROI_AT_TIME]: PartialSendHighestRoiAtTimeSchema,
} as const;

const partialSendResolver: Resolver<PartialSendFormValues> = async (
  values,
  context,
  options
) => {
  let schema: z.ZodTypeAny;
  if (!values.hasAutosend) {
    schema = PartialSendNoAutosendSchema;
  } else if (values.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
    schema = PartialSendVariantReachedMinRoiSchema;
  } else if (values.type === CriteriaType.HIGHEST_ROI_AT_TIME) {
    schema = PartialSendHighestRoiAtTimeSchema;
  } else {
    schema = PartialSendFirstToMinRoiSchema;
  }

  const result = await zodResolver(schema)(values, context, options);
  const ctx = context as {
    textingHours: { start: string; end: string; timezone: string };
    nLeftToSend: number;
  };
  const crossFieldErrors = validateAcrossFields({
    ...values,
    textingHours: ctx?.textingHours,
  });

  // Frontend-only: a partial send can't exceed the number of contacts that
  // haven't been sent to yet. Lower precedence than the field's own zod errors.
  const qty = Number(values.qty);
  if (Number.isFinite(qty) && qty > ctx?.nLeftToSend) {
    crossFieldErrors["qty"] = {
      type: "max",
      message: `Only ${ctx.nLeftToSend.toLocaleString()} left to send`,
    };
  }

  // Field-level (zod) errors win over cross-field errors on the same path: a
  // field's own validation should surface before a cross-field conflict.
  const errors = {
    ...crossFieldErrors,
    ...result.errors,
  } as typeof result.errors;

  if (Object.keys(errors).length > 0) {
    return { values: {}, errors };
  }
  return result;
};

type AutosendFormValues = Extract<PartialSendFormValues, { hasAutosend: true }>;

// Builds the { expiresAt, evaluation } payload the schedule/start mutations
// expect from the autosend form values.
function buildPartialSendEvaluation(input: AutosendFormValues) {
  const criteriaExpiresAt =
    input.type === CriteriaType.VARIANT_REACHED_MIN_ROI
      ? input.dontSendAfter
      : input.evaluationExpiresAt;
  const autosendOption =
    input.autosendOption.type === AutosendOptionType.SEND_TO_PORTION
      ? {
          type: AutosendOptionType.SEND_TO_PORTION,
          textsToSend: input.autosendOption.textsToSend ?? 1,
        }
      : { type: AutosendOptionType.SEND_TO_FULL };
  return {
    expiresAt: input.dontSendAfter,
    evaluation: EvaluationCriteriaSchema.parse({
      type: input.type,
      variantIds: input.variantIds ?? [],
      evaluationDelayInMin: input.evaluationDelayInMin ?? 0,
      expiresAt: criteriaExpiresAt,
      autosendOption,
      minRoi: input.minRoi,
      minNumberOfDonations: input.minNumberOfDonations,
      excludeDonationsOverInCents: input.excludeDonationsOverInCents,
    }),
  };
}

function AutosendCriteriaFields({
  control,
  setValue,
  onEvaluationTypeChange,
  isReadOnly,
  isProduction,
  isSingleVariant,
  textingHours,
}: {
  control: Control<PartialSendFormValues>;
  setValue: UseFormSetValue<PartialSendFormValues>;
  onEvaluationTypeChange: (newType: unknown) => void;
  isReadOnly: boolean;
  isProduction: boolean;
  isSingleVariant: boolean;
  textingHours: { start: string; end: string; timezone: string };
}): ReactElement {
  const [criteriaType, autosendOption] = useWatch({
    control,
    name: ["type", "autosendOption"],
  });
  const evaluationShape = criteriaSchemaByType[criteriaType].shape;

  const { start, end, timezone } = textingHours;
  const now = DateTime.now();
  const defaultDateTimeProps = {
    inputLabelSlotProps: {
      sx: { fontSize: "14px" },
    },
    minDate: now,
    minTime: now,
    control,
    disabled: isReadOnly,
    timezone,
  };
  const defaultTextNumericProps = {
    slotProps: {
      inputLabel: {
        sx: { fontSize: "14px" },
      },
      input: { sx: { pt: 1 } },
    },
    control,
    disabled: isReadOnly,
  };

  return (
    <>
      <Typography sx={{ mb: 2 }}>
        Configure your settings to trigger an autosend below. If your criteria
        aren't met, your campaign will pause sending.
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Typography variant="body1Bold">
              <FormattedMessage
                defaultMessage="Autosend Settings"
                description="Title for autosend settings menu"
              />
            </Typography>
          }
        />
        <CardContent>
          {!isSingleVariant && (
            <RhfSelect
              name={"type"}
              label={"Autosend Based On"}
              fullWidth
              sx={{ mb: 2, pt: 1 }}
              secondary
              onChange={onEvaluationTypeChange}
              control={control}
              disabled={isReadOnly}
            >
              {(Object.entries(criteriaTypeToLabel) as [CriteriaType, string][])
                .filter(([_, label]) => !!label)
                .map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
            </RhfSelect>
          )}

          <FlexBox gap={1} sx={{ mb: 2 }}>
            <RhfPercentageField
              fullWidth
              variant={"standard"}
              name={"minRoi"}
              label={"Minimum ROI"}
              optional={criteriaType !== CriteriaType.FIRST_TO_MIN_ROI}
              {...defaultTextNumericProps}
            />
            <RhfNumberField
              fullWidth
              type="number"
              variant={"standard"}
              name={"minNumberOfDonations"}
              label={"Minimum Number of Donations"}
              optional={evaluationShape?.minNumberOfDonations?.isOptional()}
              {...defaultTextNumericProps}
            />
          </FlexBox>
          <FlexBox gap={2} sx={{ mb: 2 }}>
            <RhfCurrencyField
              fullWidth
              type="number"
              variant="standard"
              label={"Exclude Donations Over"}
              name={"excludeDonationsOverInCents"}
              adornmentOffset={0}
              optional={evaluationShape?.excludeDonationsOverInCents?.isOptional()}
              {...defaultTextNumericProps}
            />
            {criteriaType === CriteriaType.FIRST_TO_MIN_ROI && (
              <RhfSelect
                fullWidth
                secondary
                label={"Start Checking for Winner"}
                name="evaluationDelayInMin"
                sx={{ pt: 1 }}
                control={control}
                disabled={isReadOnly}
                rules={{ deps: ["evaluationExpiresAt"] }}
              >
                <MenuItem key={"immediate"} value={0}>
                  Immediately
                </MenuItem>
                {!isProduction && <MenuItem value={5}>5 min</MenuItem>}
                {[1, 2, 3, 4, 5, 6, 7, 8].map(hours => (
                  <MenuItem key={hours} value={hours * 60}>
                    after {hours} {hours === 1 ? "hour" : "hours"}
                  </MenuItem>
                ))}
              </RhfSelect>
            )}
          </FlexBox>
          {criteriaType === CriteriaType.FIRST_TO_MIN_ROI && (
            <>
              <RhfDateTimePicker
                name={"evaluationExpiresAt"}
                dateLabel={`Date to Stop Checking for a Winner`}
                timeLabel={"Time to Stop Checking for a Winner"}
                variant="standard"
                rules={{ deps: ["dontSendAfter"] }}
                {...defaultDateTimeProps}
              />
            </>
          )}
          {criteriaType === CriteriaType.HIGHEST_ROI_AT_TIME && (
            <>
              <RhfDateTimePicker
                name={"evaluationExpiresAt"}
                dateLabel={`Date to Pick a Winner`}
                timeLabel={"Time to Pick a Winner"}
                variant="standard"
                rules={{ deps: ["dontSendAfter"] }}
                {...defaultDateTimeProps}
              />
            </>
          )}
          <RhfSelect
            name={"autosendOption.type"}
            label={"Autosend Option"}
            fullWidth
            sx={{ pt: 1 }}
            secondary
            control={control}
            disabled={isReadOnly}
            onChange={value => {
              // MenuItem value can only be a string so we set the rhf value here.
              if (value === AutosendOptionType.SEND_TO_PORTION) {
                setValue("autosendOption", {
                  type: AutosendOptionType.SEND_TO_PORTION,
                  textsToSend: 1,
                });
              } else {
                setValue("autosendOption", {
                  type: AutosendOptionType.SEND_TO_FULL,
                });
              }
            }}
            defaultValue={AutosendOptionType.SEND_TO_FULL}
          >
            <MenuItem value="SEND_TO_FULL">
              {`Send ${criteriaType === CriteriaType.VARIANT_REACHED_MIN_ROI ? "message" : "the winner"} to remainder of the list`}
            </MenuItem>
            <MenuItem value="SEND_TO_PORTION">
              {`Send ${criteriaType === CriteriaType.VARIANT_REACHED_MIN_ROI ? "message" : "the winner"} to a portion of the list, then pause`}
            </MenuItem>
          </RhfSelect>
          {autosendOption?.type === AutosendOptionType.SEND_TO_PORTION && (
            <RhfNumberField
              fullWidth
              type="number"
              variant={"standard"}
              name={"autosendOption.textsToSend"}
              label={"Texts to Send"}
              {...defaultTextNumericProps}
            />
          )}
        </CardContent>
      </Card>

      <Typography variant="body2" pb={2}>
        <Typography variant="body2Bold" component="span">
          Texting Hours:{" "}
        </Typography>
        {formatShortTime(start)} - {formatShortTime(end)} (
        {timezoneDisplayName(timezone)})
        <Typography>
          A winner will only be sent within your campaign texting hours. If a
          winner is reached after the end of the texting hours, the send will
          pause and then resume at the start of the next day's texting hours.
        </Typography>
      </Typography>
      <Typography variant="body2Bold">Don't Send After</Typography>
      <Typography pb={2}>
        If autosend is not started by this date and time, the campaign will
        pause until an admin manually resumes sending or ends the campaign.
      </Typography>
      <RhfDateTimePicker
        name={"dontSendAfter"}
        dateLabel={"Don't Send After Date"}
        timeLabel={"Don't Send After Time"}
        minTime={now}
        minDate={now}
        control={control}
        disabled={isReadOnly}
        timezone={timezone}
        rules={{ deps: ["evaluationExpiresAt"] }}
      />
    </>
  );
}

export function PartialSendDialog({
  campaign,
  handleClose,
}: {
  campaign: CampaignForView;
  handleClose: () => void;
}): ReactElement {
  const isSingleVariant =
    campaign.scriptVariants.filter(s => !s.deletedAt).length === 1;
  const isProduction = import.meta.env.STW_ENV_NAME === "production";
  const subaccount = useSubaccountAdminConfig()!;
  const confirm = useConfirm();
  const [canceling, setCanceling] = useState(false);

  const start = campaign.textingHoursStart ?? subaccount.textingHoursStart;
  const end = campaign.textingHoursEnd ?? subaccount.textingHoursEnd;
  const timezone =
    campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone;

  const textingHours = { start, end, timezone };

  const nLeftToSend = campaign.quickStats
    ? campaign.quickStats.contactCount - campaign.quickStats.initialSentCount
    : 0;

  const { isInTextingHours } = useTextingHours(textingHours);

  const activeEvaluation = campaign.partialSendEvaluations.find(
    e => e.series === campaign.partialSendEvaluationSeries
  );
  const isReadOnly = !!activeEvaluation;

  const [sendPartial] = useMutation<
    StartCampaignPartialInitialSendMutation,
    StartCampaignPartialInitialSendMutationVariables
  >(START_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start initial send",
  });

  const [schedulePartial] = useMutation<
    ScheduleCampaignPartialInitialSendMutation,
    ScheduleCampaignPartialInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule initial send",
  });

  const [cancelPartialEvaluation] = useMutation<
    CancelCampaignPartialEvaluationMutation,
    CancelCampaignPartialEvaluationMutationVariables
  >(CANCEL_CAMPAIGN_PARTIAL_EVALUATION, {
    variables: { input: { campaignId: campaign.id } },
    onErrorSnackbarMessage: "Failed to cancel partial send evaluation",
  });

  const defaultValues = (() => {
    if (!activeEvaluation) {
      return { hasAutosend: false as const };
    }
    const criteria = EvaluationCriteriaSchema.parse(
      activeEvaluation.criteria
    ) as EvaluationCriteriaOutput;
    return {
      hasAutosend: true as const,
      qty: activeEvaluation.batchSize,
      dontSendAfter: campaign.partialSendEvaluationExpiresAt,
      type: criteria.type,
      variantIds: criteria.variantIds,
      evaluationDelayInMin: criteria.evaluationDelayInMin,
      evaluationExpiresAt:
        criteria.type === CriteriaType.VARIANT_REACHED_MIN_ROI
          ? undefined
          : criteria.expiresAt,
      autosendOption: criteria.autosendOption,
      minRoi: criteria.minRoi,
      minNumberOfDonations:
        criteria.minNumberOfDonations > 0
          ? criteria.minNumberOfDonations
          : null,
      excludeDonationsOverInCents: criteria.excludeDonationsOverInCents,
    };
  })();

  const {
    control,
    getValues,
    setValue,
    reset,
    handleSubmit,
    clearErrors,
    trigger,
    formState: { isSubmitting },
  } = useForm<PartialSendFormValues>({
    resolver: partialSendResolver,
    reValidateMode: "onChange",
    mode: "all",
    context: { textingHours, nLeftToSend },
    defaultValues: defaultValues as any,
  });

  async function onSubmit(input: PartialSendFormValues) {
    const partialSendEvaluations = input.hasAutosend
      ? buildPartialSendEvaluation(input)
      : null;

    if (input.scheduleFor) {
      await schedulePartial({
        variables: {
          input: {
            campaignId: campaign.id,
            qty: input.qty,
            scheduleFor: input.scheduleFor,
            partialSendEvaluations,
          },
        },
      });
    } else {
      await sendPartial({
        variables: {
          input: {
            campaignId: campaign.id,
            qty: input.qty,
            partialSendEvaluations,
          },
        },
      });
    }

    handleClose();
  }

  const handleCancel = useCallback(async () => {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Cancel partial send evaluation",
      description:
        "Are you sure you want to cancel the partial send evaluation? The campaign will return to ready state.",
      confirmationText: "Cancel evaluation",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await cancelPartialEvaluation();
    setCanceling(false);
  }, [canceling, confirm, cancelPartialEvaluation]);

  const [hasAutosend, scheduleFor] = useWatch({
    control,
    name: ["hasAutosend", "scheduleFor"],
  });
  const isScheduled = !!scheduleFor || !isInTextingHours;

  function handleToggleAutosend() {
    if (getValues("hasAutosend")) {
      setValue("hasAutosend", false);
      return;
    }
    const ids = campaign.scriptVariants
      .filter(s => !s.deletedAt)
      .map(s => s.id);
    reset({
      ...getValues(),
      hasAutosend: true,
      variantIds: ids,
      autosendOption: { type: AutosendOptionType.SEND_TO_FULL },
      type: isSingleVariant
        ? CriteriaType.VARIANT_REACHED_MIN_ROI
        : CriteriaType.FIRST_TO_MIN_ROI,
      evaluationDelayInMin: isSingleVariant ? 0 : 180,
    } as DefaultValues<PartialSendFormValues>);
  }

  function handleEvaluationTypeChange(newType: unknown) {
    const type = newType as CriteriaType;
    setValue("type", type, { shouldValidate: false });
    // The evaluation delay only applies to FIRST_TO_MIN_ROI.
    if (type === CriteriaType.FIRST_TO_MIN_ROI) {
      setValue("evaluationDelayInMin", 180, { shouldValidate: false });
    } else {
      setValue("evaluationDelayInMin", 0, { shouldValidate: false });
    }
    // Drop errors from the previous criteria type and re-validate for the new
    // one. Touched state is kept, so already-visited fields still surface their
    // errors while not-yet-touched ones stay hidden.
    clearErrors();
    void trigger(["dontSendAfter", "evaluationExpiresAt", "scheduleFor"]);
  }

  return (
    <Dialog open={true} onClose={handleClose} fullWidth maxWidth={"md"}>
      <DialogTitle sx={{ pb: 0, mb: 0 }}>
        Partial Send on {campaign.name}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography sx={{ mb: 2 }} variant="body1">
            Enter how many initial texts you want to send for this portion and
            confirm that the send summary information is correct, then click
            "send" to start your campaign.
          </Typography>
          {isSingleVariant && (
            <Typography sx={{ mb: 2 }}>
              Note that sending across script variants will roughly split your
              list in half, but can't guarantee an exact 50/50 split.
            </Typography>
          )}
          <RhfNumberField
            control={control}
            name={"qty"}
            fullWidth
            label={"Texts to Send"}
            disabled={isReadOnly}
          />
          <RhfDateTimePicker
            name={"scheduleFor"}
            dateLabel={"Initial Send Date"}
            timeLabel={"Initial Send Time"}
            rules={{ deps: ["evaluationExpiresAt"] }}
            control={control}
            disabled={isReadOnly}
            timezone={timezone}
            minDate={DateTime.now()}
            minTime={DateTime.now()}
          />
          {hasAutosend && (
            <AutosendCriteriaFields
              control={control}
              setValue={setValue}
              onEvaluationTypeChange={handleEvaluationTypeChange}
              isReadOnly={isReadOnly}
              isProduction={isProduction}
              isSingleVariant={isSingleVariant}
              textingHours={textingHours}
            />
          )}

          <SendSummaryCard campaign={campaign} />

          <DialogActions
            sx={{ justifyContent: "space-between", pl: 0, pr: 0, pt: 2 }}
          >
            {isReadOnly ? (
              <>
                <Button variant="outlined" onClick={handleClose}>
                  CLOSE
                </Button>
                <Button variant="contained" onClick={handleCancel}>
                  Cancel Evaluation
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={handleToggleAutosend}
                  color="tertiary"
                >
                  {hasAutosend ? "REMOVE AUTOSEND" : "ADD AUTOSEND"}
                </Button>
                <FlexBox gap={1}>
                  <Button variant="outlined" onClick={handleClose}>
                    CLOSE
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {isScheduled ? "SCHEDULE SEND" : "SEND NOW"}
                  </Button>
                </FlexBox>
              </>
            )}
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}
